/**
 * Pure helpers for BulkResolveWithValueDialog: table row formatting, per-row
 * target computation for "make every selected row agree with one source",
 * and the mutation grouping/summary math. Structural types, like
 * resolveWithValue.helpers, so tests don't need codegen fixtures.
 */
import {
  formatFieldValue,
  getFieldLabel,
  type SavedStatusCounts,
  type SkipGroup,
} from "./DiscrepancyReviewDialog.helpers";
import {
  computeCopyDefault,
  computeDocumentEligibility,
  computeVendorEligibility,
  type ResolutionTargetLiteral,
  type ResolveRow,
} from "./resolveWithValue.helpers";
import {
  copiesLeftDisagreeing,
  predictRowOutcome,
  type CopyKey,
  type RowOutcome,
} from "./resolveWithValue.outcome";

export type BulkSource = "EMANIFEST" | "VENDOR" | "DOCUMENT";
export type BulkAnswer = BulkSource | "DISMISS" | "CANT_DETERMINE";

const BULK_ANSWER_VALUES: readonly string[] = [
  "EMANIFEST",
  "VENDOR",
  "DOCUMENT",
  "DISMISS",
  "CANT_DETERMINE",
];

/** Narrows a RadioGroup's plain string change value to the BulkAnswer union. */
export function isBulkAnswer(value: string): value is BulkAnswer {
  return BULK_ANSWER_VALUES.includes(value);
}

export function isBulkSource(answer: BulkAnswer): answer is BulkSource {
  return answer === "EMANIFEST" || answer === "VENDOR" || answer === "DOCUMENT";
}

/** The extra field the table's Manifest column needs beyond ResolveRow. */
export interface BulkResolveRow extends ResolveRow {
  manifestTrackingNumber?: string | null;
}

// ---------------------------------------------------------------------------
// Title / table
// ---------------------------------------------------------------------------

/** Title Case, matching the single-row dialog's "Resolve · <field>" register. */
export function formatBulkTitle(rowCount: number): string {
  return `Resolve ${rowCount} Discrepancies`;
}

export interface BulkTableRow {
  id: string;
  manifest: string;
  field: string;
  line: string;
  eManifest: string;
  vendor: string;
  document: string;
}

/** One table row per selected discrepancy, formatted the way the single-row dialog formats its values (waste codes, null → "—"). */
export function formatBulkTableRows(rows: readonly BulkResolveRow[]): BulkTableRow[] {
  return rows.map((row) => ({
    id: row.id,
    manifest: row.manifestTrackingNumber ?? "—",
    field: getFieldLabel(row.field),
    line: row.wasteLineUnifiedId ? (row.wasteLineNumber ?? "—") : "—",
    eManifest: formatFieldValue(row.field, row.eManifestValue),
    vendor: formatFieldValue(row.field, row.vendorValue),
    document: formatFieldValue(row.field, row.manifestDocumentValue),
  }));
}

// ---------------------------------------------------------------------------
// Radios: availability / disabled reasons
// ---------------------------------------------------------------------------

/** Name of each source's copy in skip reasons and radio reasons. */
const SOURCE_NAMES: Record<BulkSource, string> = {
  EMANIFEST: "e-Manifest",
  VENDOR: "vendor",
  DOCUMENT: "document",
};

/** Why "Use <source>'s value for all" is disabled: no selected row has a value from that source. Null otherwise. */
export function sourceDisabledReason(
  rows: readonly BulkResolveRow[],
  source: BulkSource
): string | null {
  const name = SOURCE_NAMES[source];
  const article = source === "EMANIFEST" ? "an" : "a";
  return rows.some((r) => answerValueForSource(source, r) != null)
    ? null
    : `No selected row has ${article} ${name} value`;
}

/** Rows that can still take an answer: only Pending Review rows do. */
export function answerableRows<T extends { status: string }>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.status === "UNRESOLVED");
}

export const ALREADY_DECIDED_REASON = "already has a decision";

// ---------------------------------------------------------------------------
// Per-row plan for a chosen source
// ---------------------------------------------------------------------------

export interface BulkRowPlan {
  id: string;
  applyTo: ResolutionTargetLiteral[];
}

export interface BulkPlan {
  updatable: BulkRowPlan[];
  skips: SkipGroup[];
  /** Per-row skip reasons, in row order; `skips` is these grouped and counted. */
  skippedRows: { id: string; reason: string }[];
}

/** Every copy the plan can write. */
export const ALL_BULK_TARGETS: readonly ResolutionTargetLiteral[] = [
  "VENDOR_DATA",
  "EMANIFEST",
  "DOCUMENT",
];

export function answerValueForSource(source: BulkSource, row: BulkResolveRow): string | null {
  if (source === "EMANIFEST") return row.eManifestValue ?? null;
  if (source === "VENDOR") return row.vendorValue ?? null;
  return row.manifestDocumentValue ?? null;
}

/** Reuses computeCopyDefault (the single-row dialog's own normalization) for one row instead of a batch. */
export function copyDiffers(
  row: BulkResolveRow,
  getCurrent: (r: ResolveRow) => string | null | undefined,
  answerValue: string | null
): boolean {
  return computeCopyDefault([row], getCurrent, () => answerValue);
}

interface RowTarget {
  included: boolean;
  skipReason?: string;
}

/** VENDOR_DATA target for one row: differs from the source value AND passes computeVendorEligibility. */
function vendorTargetFor(
  row: BulkResolveRow,
  source: BulkSource,
  answerValue: string | null
): RowTarget {
  if (!copyDiffers(row, (r) => r.vendorValue, answerValue)) return { included: false };
  const { eligibleIds, skips } = computeVendorEligibility(
    [row],
    source === "DOCUMENT",
    "document's value"
  );
  if (eligibleIds.length > 0) return { included: true };
  return { included: false, skipReason: skips[0]?.reason ?? "vendor data can't be updated" };
}

/** DOCUMENT target for one row: has a document value, differs from the source value, and passes computeDocumentEligibility. */
function documentTargetFor(row: BulkResolveRow, answerValue: string | null): RowTarget {
  if (row.manifestDocumentValue == null) return { included: false };
  if (!copyDiffers(row, (r) => r.manifestDocumentValue, answerValue)) return { included: false };
  const { eligibleIds, skips } = computeDocumentEligibility([row]);
  if (eligibleIds.length > 0) return { included: true };
  return { included: false, skipReason: skips[0]?.reason ?? "document can't be updated" };
}

type RowPlanResult = { applyTo: ResolutionTargetLiteral[] } | { skipReason: string };

/**
 * One row's applyTo targets for the chosen source, or a skip reason when none
 * apply. EMANIFEST never gates (any difference marks it pending); VENDOR_DATA
 * and DOCUMENT each need their own eligibility check.
 */
function planRow(row: BulkResolveRow, source: BulkSource): RowPlanResult {
  if (row.status !== "UNRESOLVED") return { skipReason: ALREADY_DECIDED_REASON };

  const answerValue = answerValueForSource(source, row);
  if (answerValue == null) return { skipReason: `has no ${SOURCE_NAMES[source]} value` };
  const applyTo: ResolutionTargetLiteral[] = [];
  const reasons: string[] = [];
  const vendor = vendorTargetFor(row, source, answerValue);
  const eManifestDiffers = copyDiffers(row, (r) => r.eManifestValue, answerValue);
  const document = documentTargetFor(row, answerValue);
  const candidates: [ResolutionTargetLiteral, boolean, string | undefined][] = [
    ["VENDOR_DATA", vendor.included, vendor.skipReason],
    ["EMANIFEST", eManifestDiffers, undefined],
    ["DOCUMENT", document.included, document.skipReason],
  ];
  for (const [target, included, skipReason] of candidates) {
    if (included) applyTo.push(target);
    else if (skipReason) reasons.push(skipReason);
  }

  if (applyTo.length > 0) return { applyTo: [...applyTo].sort() };
  if (reasons.length > 0) return { skipReason: reasons[0] };
  return { skipReason: "already matches" };
}

/**
 * Every selected row's plan for the chosen source: which rows get updated
 * (and with which targets), which get skipped (and why), the latter grouped
 * like computeVendorEligibility's skip summary.
 */
export function computeBulkPlan(rows: readonly BulkResolveRow[], source: BulkSource): BulkPlan {
  const updatable: BulkRowPlan[] = [];
  const skippedRows: { id: string; reason: string }[] = [];
  const skipCounts = new Map<string, number>();

  for (const row of rows) {
    const result = planRow(row, source);
    if ("skipReason" in result) {
      skippedRows.push({ id: row.id, reason: result.skipReason });
      skipCounts.set(result.skipReason, (skipCounts.get(result.skipReason) ?? 0) + 1);
    } else updatable.push({ id: row.id, applyTo: result.applyTo });
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
  return { updatable, skips, skippedRows };
}

// ---------------------------------------------------------------------------
// Mutation grouping / summary
// ---------------------------------------------------------------------------

export interface BulkMutationGroup {
  ids: string[];
  applyTo: ResolutionTargetLiteral[];
}

/** Groups updatable rows by their (already-sorted) applyTo set, so each distinct set becomes one mutation call. */
export function groupByApplyTo(updatable: readonly BulkRowPlan[]): BulkMutationGroup[] {
  const groups = new Map<string, BulkMutationGroup>();
  for (const row of updatable) {
    const key = row.applyTo.join(",");
    const existing = groups.get(key);
    if (existing) existing.ids.push(row.id);
    else groups.set(key, { ids: [row.id], applyTo: row.applyTo });
  }
  return [...groups.values()];
}

export interface BulkRowOutcome {
  outcome: RowOutcome;
  /** Copies still disagreeing with the answer after the writes. */
  left: CopyKey[];
}

/** The predicted outcome of every updatable row, by id. */
export function computeBulkOutcomes(
  rows: readonly BulkResolveRow[],
  source: BulkSource,
  plan: BulkPlan
): Map<string, BulkRowOutcome> {
  const rowsById = new Map(rows.map((r) => [r.id, r]));
  const outcomes = new Map<string, BulkRowOutcome>();
  for (const { id, applyTo } of plan.updatable) {
    const row = rowsById.get(id);
    if (!row) continue;
    const answerValue = answerValueForSource(source, row);
    outcomes.set(id, {
      outcome: predictRowOutcome(row, answerValue, applyTo),
      left: copiesLeftDisagreeing(row, answerValue, applyTo),
    });
  }
  return outcomes;
}

export type BulkOutcomeCounts = SavedStatusCounts;

export function countBulkOutcomes(
  outcomes: ReadonlyMap<string, BulkRowOutcome>
): BulkOutcomeCounts {
  const counts: BulkOutcomeCounts = { resolved: 0, pending: 0, stayOpen: 0 };
  for (const { outcome } of outcomes.values()) {
    if (outcome === "RESOLVED") counts.resolved += 1;
    else if (outcome === "PENDING") counts.pending += 1;
    else counts.stayOpen += 1;
  }
  return counts;
}

/** "X Resolved, Y Fix pending, Z <stayOpenPhrase>", leaving out zero parts. */
function outcomeParts(counts: BulkOutcomeCounts, stayOpenPhrase: (n: number) => string): string {
  return [
    counts.resolved > 0 ? `${counts.resolved} Resolved` : null,
    counts.pending > 0 ? `${counts.pending} Fix pending` : null,
    counts.stayOpen > 0 ? `${counts.stayOpen} ${stayOpenPhrase(counts.stayOpen)}` : null,
  ]
    .filter((part): part is string => part != null)
    .join(", ");
}

export function formatBulkToastMessage(counts: BulkOutcomeCounts, skippedCount: number): string {
  const total = counts.resolved + counts.pending + counts.stayOpen;
  const noun = total === 1 ? "row" : "rows";
  const parts = outcomeParts(counts, () => "still Pending Review");
  const base = parts ? `Saved ${total} ${noun}: ${parts}.` : `Saved ${total} ${noun}.`;
  return skippedCount > 0 ? `${base} ${skippedCount} skipped.` : base;
}

export function formatBulkErrorMessage(succeeded: number): string {
  if (succeeded === 0) return "Failed to resolve the discrepancies.";
  const noun = succeeded === 1 ? "row" : "rows";
  return `Saved ${succeeded} ${noun} before an error. The rest were not changed.`;
}
